import { CommunicationChartNodeKindEnum } from '@web/components/CommunicationNetworkGraph/core/communicationChart/nodeKind';
import { COMMUNICATION_CHART_OPACITY } from '@web/components/CommunicationNetworkGraph/graph/communicationChartConstants';
export var COMMUNICATION_CHART_MOTION = {
    hoverScale: 1.05,
    hoverDurationMs: 150,
    selectionTransitionMs: 200,
    entranceNodeDurationMs: 220,
    entranceNodeStaggerMs: 40,
    entranceEdgeDelayMs: 80,
    entranceEdgeDurationMs: 280,
    defaultEdgeOpacity: COMMUNICATION_CHART_OPACITY.edge
};
export var COMMUNICATION_CHART_CYTOSCAPE_TRANSITION = {
    'transition-property': 'opacity, width, height, border-width',
    'transition-duration': COMMUNICATION_CHART_MOTION.selectionTransitionMs,
    'transition-timing-function': 'ease-out'
};
var CANVAS_PAINTED_NODE_CLASSES = [
    'personNode',
    CommunicationChartNodeKindEnum.External,
    CommunicationChartNodeKindEnum.CrossTeamAggregate,
    CommunicationChartNodeKindEnum.InternalMeet,
    CommunicationChartNodeKindEnum.CrossTeamSpaceChat,
    CommunicationChartNodeKindEnum.ExternalSpaceChat,
    CommunicationChartNodeKindEnum.Other
];
var nodeHasAnyClass = function (node, classNames) {
    for (var _i = 0, classNames_1 = classNames; _i < classNames_1.length; _i++) {
        var className = classNames_1[_i];
        if (node.hasClass(className)) {
            return true;
        }
    }
    return false;
};
var usesCommunicationChartCanvasEntranceNode = function (node) { return node.hasClass('personNode') || node.hasClass('teamHubSatellite'); };
var motionReducedCache = null;
export var isCommunicationChartMotionReduced = function () {
    if (motionReducedCache !== null) {
        return motionReducedCache;
    }
    if (typeof window === 'undefined' || !window.matchMedia) {
        motionReducedCache = false;
        return false;
    }
    motionReducedCache = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    return motionReducedCache;
};
export var usesCommunicationChartCanvasNodePaint = function (node) { return nodeHasAnyClass(node, CANVAS_PAINTED_NODE_CLASSES); };
export var resolveCommunicationChartNodeHoverScale = function (node) {
    return node.hasClass('hovered') && !node.hasClass('dimmed')
        ? COMMUNICATION_CHART_MOTION.hoverScale
        : 1;
};
var easeOutCubic = function (progress) { return 1 - Math.pow(1 - progress, 3); };
export var playCommunicationChartEntranceAnimation = function (cy, redrawOverlay) {
    var cancelled = { value: false };
    var timeouts = [];
    var activeOverlayAnimations = [];
    var overlayRafId = null;
    var cancel = function () {
        cancelled.value = true;
        if (overlayRafId !== null) {
            cancelAnimationFrame(overlayRafId);
            overlayRafId = null;
        }
        timeouts.forEach(function (timeoutId) { return clearTimeout(timeoutId); });
        activeOverlayAnimations.length = 0;
        cy.nodes().forEach(function (node) {
            node.removeData('overlayOpacity');
        });
        cy.nodes().stop(true, true);
        cy.edges().stop(true, true);
        cy.edges().removeStyle('opacity');
    };
    if (isCommunicationChartMotionReduced()) {
        return { cancel: cancel };
    }
    var nodes = cy.nodes();
    var edges = cy.edges();
    nodes.forEach(function (node) {
        node.data('overlayOpacity', 0);
        if (!usesCommunicationChartCanvasEntranceNode(node)) {
            node.style('opacity', 0);
        }
    });
    edges.forEach(function (edge) {
        if (!edge.hasClass('teamHubEdge')) {
            edge.style('opacity', 0);
        }
    });
    redrawOverlay();
    var tickOverlayAnimations = function (now) {
        if (cancelled.value) {
            return;
        }
        var hasActive = false;
        var nextAnimations = [];
        for (var _i = 0, activeOverlayAnimations_1 = activeOverlayAnimations; _i < activeOverlayAnimations_1.length; _i++) {
            var animation = activeOverlayAnimations_1[_i];
            var progress = Math.min(1, (now - animation.startTime) / animation.duration);
            if (progress >= 1) {
                animation.node.removeData('overlayOpacity');
            }
            else {
                animation.node.data('overlayOpacity', easeOutCubic(progress));
                nextAnimations.push(animation);
                hasActive = true;
            }
        }
        activeOverlayAnimations.length = 0;
        activeOverlayAnimations.push.apply(activeOverlayAnimations, nextAnimations);
        redrawOverlay();
        if (hasActive) {
            overlayRafId = requestAnimationFrame(tickOverlayAnimations);
        }
        else {
            overlayRafId = null;
        }
    };
    var startOverlayAnimation = function (node) {
        activeOverlayAnimations.push({
            node: node,
            startTime: performance.now(),
            duration: COMMUNICATION_CHART_MOTION.entranceNodeDurationMs
        });
        if (overlayRafId === null) {
            overlayRafId = requestAnimationFrame(tickOverlayAnimations);
        }
    };
    nodes.forEach(function (node, index) {
        var delay = index * COMMUNICATION_CHART_MOTION.entranceNodeStaggerMs;
        var timeoutId = window.setTimeout(function () {
            if (cancelled.value) {
                return;
            }
            startOverlayAnimation(node);
            if (!usesCommunicationChartCanvasEntranceNode(node)) {
                node.animate({
                    style: { opacity: 1 },
                    duration: COMMUNICATION_CHART_MOTION.entranceNodeDurationMs,
                    easing: 'ease-out'
                });
            }
        }, delay);
        timeouts.push(timeoutId);
    });
    var edgeDelay = Math.min(nodes.length * COMMUNICATION_CHART_MOTION.entranceNodeStaggerMs, 600) + COMMUNICATION_CHART_MOTION.entranceEdgeDelayMs;
    var edgeTimeoutId = window.setTimeout(function () {
        if (cancelled.value) {
            return;
        }
        edges
            .filter(function (edge) { return !edge.hasClass('teamHubEdge'); })
            .animate({
            style: { opacity: COMMUNICATION_CHART_MOTION.defaultEdgeOpacity },
            duration: COMMUNICATION_CHART_MOTION.entranceEdgeDurationMs,
            easing: 'ease-out'
        });
    }, edgeDelay);
    timeouts.push(edgeTimeoutId);
    return { cancel: cancel };
};
var animateCommunicationChartNodeHoverSize = function (node, scale) {
    if (isCommunicationChartMotionReduced() ||
        usesCommunicationChartCanvasNodePaint(node)) {
        return;
    }
    if (scale !== 1 && node.hasClass('dimmed')) {
        return;
    }
    var baseSize = Number(node.data('nodeSize'));
    if (!Number.isFinite(baseSize)) {
        return;
    }
    node.stop(false, true);
    node.animate({
        style: {
            width: baseSize * scale,
            height: baseSize * scale
        },
        duration: COMMUNICATION_CHART_MOTION.hoverDurationMs,
        easing: 'ease-out'
    });
};
export var animateCommunicationChartNodeHoverIn = function (node) {
    animateCommunicationChartNodeHoverSize(node, COMMUNICATION_CHART_MOTION.hoverScale);
};
export var animateCommunicationChartNodeHoverOut = function (node) {
    animateCommunicationChartNodeHoverSize(node, 1);
};
export var resolveCommunicationChartOverlayOpacity = function (node) {
    var entranceOpacity = node.data('overlayOpacity');
    var baseOpacity = node.hasClass('dimmed')
        ? COMMUNICATION_CHART_OPACITY.nodeDimmedCanvas
        : 1;
    if (typeof entranceOpacity === 'number') {
        return entranceOpacity * baseOpacity;
    }
    return baseOpacity;
};
